import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import { Weather } from "@/types/weather";
import dayjs from "dayjs";
import { Button, Chip, Skeleton, Spinner } from "@heroui/react";
import {
    BarChart,
    Bar,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    ReferenceArea,
    Label,
} from "recharts";
import { roundTo } from "@/lib/utils/numbers";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import useResizeObserver from "@/lib/utils/useResizeObserver";
import { GDD } from "@/types/field";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

function WeatherCharts() {
    const { t } = useTranslation("insights");
    const { formatDateMMDDYYYY } = useI18n();
    const { useGetRequest } = useRequest();
    const { formatUnit } = useI18n();

    const { fieldId, selectedCropCycle, fieldIsComplete, fieldIsAdvanced } =
        useFieldDashboard() ?? {};
    const f = useFormContext();
    const [startDate, endDate] = f.watch(["startDate", "endDate"]);

    const lastDayOfGraph = useMemo(() => {
        return dayjs(endDate);
    }, [endDate]);

    const weatherQuery = useGetRequest<Weather[]>({
        url: `/fields/${fieldId}/weather/`,
        enabled: !!fieldId && !!selectedCropCycle,
        params: {
            start_date: dayjs
                .min(dayjs(selectedCropCycle?.start_date), dayjs(startDate))
                .format("YYYY-MM-DD"),
            end_date: lastDayOfGraph.format("YYYY-MM-DD"),
        },
    });

    const gddQuery = useGetRequest<GDD[]>({
        url: `/fields/${fieldId}/gdd/`,
        enabled: !!fieldId && !!selectedCropCycle,
    });

    const gddData = useMemo(() => {
        if (!gddQuery.data) return [];
        return gddQuery.data
            .filter((d) => {
                const date = dayjs(d.date);
                return (
                    date.isSameOrAfter(startDate, "day") &&
                    date.isSameOrBefore(dayjs(endDate))
                );
            })
            .map((row) => ({
                ...row,
                date: dayjs(row.date).toDate(),
            }));
    }, [gddQuery.data, endDate, startDate]);

    const weatherData = useMemo(() => {
        return sortArrayByKey(
            weatherQuery.data?.filter((d) => {
                const date = dayjs(d.date);
                return (
                    date.isSameOrAfter(startDate, "day") &&
                    date.isSameOrBefore(dayjs(endDate))
                );
            }) ?? [],
            "date",
        ).map((row) => ({
            ...row,
            date: dayjs(row.date).toDate(),
        }));
    }, [endDate, startDate, weatherQuery.data]);

    const { elementRef, isResizing } = useResizeObserver(250);

    const forecastReferenceArea = useMemo(() => {
        const maxWeatherValue = dayjs.max(
            weatherData.map((d) => dayjs(d.date)),
        );
        if (!maxWeatherValue) return null;

        return (
            <>
                <ReferenceArea
                    x1={getTodayWithTimeTravel()
                        .endOf("day")
                        .toDate()
                        .getTime()}
                    x2={dayjs
                        .min(
                            maxWeatherValue,
                            getTodayWithTimeTravel().add(14, "day"),
                        )
                        .toDate()
                        .getTime()}
                    stroke="green"
                    strokeOpacity={0.5}
                    fill="green"
                    fillOpacity={0.05}
                >
                    <Label
                        value="Forecasted"
                        position="top"
                        fontSize={10}
                        dx={-14}
                    />
                </ReferenceArea>
                <ReferenceArea
                    x1={getTodayWithTimeTravel()
                        .add(14, "day")
                        .endOf("day")
                        .toDate()
                        .getTime()}
                    x2={dayjs
                        .min(
                            maxWeatherValue,
                            getTodayWithTimeTravel().add(35, "day"),
                        )
                        .toDate()
                        .getTime()}
                    stroke="blue"
                    strokeOpacity={0.5}
                    fill="blue"
                    fillOpacity={0.05}
                >
                    <Label value="Extended" position="top" fontSize={10} />
                </ReferenceArea>
                <ReferenceArea
                    x1={getTodayWithTimeTravel()
                        .add(35, "day")
                        .endOf("day")
                        .toDate()
                        .getTime()}
                    x2={dayjs
                        .min(
                            maxWeatherValue,
                            getTodayWithTimeTravel().add(215, "day"),
                        )
                        .toDate()
                        .getTime()}
                    stroke="#FFA500"
                    strokeOpacity={0.5}
                    fill="#FFA500"
                    fillOpacity={0.05}
                >
                    <Label value="Seasonal" position="top" fontSize={10} />
                </ReferenceArea>
            </>
        );
    }, [weatherData]);

    const showingForecast = useMemo(() => {
        return (
            dayjs(endDate).isSameOrBefore(dayjs().add(14, "day")) &&
            dayjs(endDate).isAfter(dayjs())
        );
    }, [endDate]);

    const showingExtendedForecast = useMemo(() => {
        return (
            dayjs(endDate).isAfter(dayjs().add(14, "day")) &&
            dayjs(endDate).isSameOrBefore(dayjs().add(35, "day"))
        );
    }, [endDate]);
    const showingSeasonalForecast = useMemo(() => {
        return dayjs(endDate).isAfter(dayjs().add(35, "day"));
    }, [endDate]);

    const precipSum = useMemo(() => {
        return roundTo(
            weatherData.reduce((acc, curr) => acc + curr.precipitation, 0),
            2,
        );
    }, [weatherData]);

    return (
        <div className="w-full h-[38rem]" ref={elementRef}>
            {isResizing ? (
                <Skeleton className="h-full w-full" />
            ) : weatherQuery.isLoading || gddQuery.isLoading ? (
                <div className="flex justify-center items-center h-full">
                    <Spinner label={t("messages.loadingWeatherData")} />
                </div>
            ) : !weatherData.length ? (
                <div className="bg-gray-100 h-full rounded-sm">
                    <div className=" p-3 italic text-center">
                        {t("messages.noDataSelectedDates")}
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex flex-row justify-between">
                        <div className="flex flex-row gap-2">
                            <Button
                                color={showingForecast ? "primary" : "default"}
                                onPress={() =>
                                    f.setValue(
                                        "endDate",
                                        getTodayWithTimeTravel()
                                            .add(14, "day")
                                            .toDate(),
                                    )
                                }
                                size="sm"
                                startContent={
                                    showingForecast && (
                                        <FontAwesomeIcon icon={faCheck} />
                                    )
                                }
                            >
                                Forecast
                            </Button>
                            {(fieldIsAdvanced || fieldIsComplete) && (
                                <Button
                                    color={
                                        showingExtendedForecast
                                            ? "primary"
                                            : "default"
                                    }
                                    onPress={() =>
                                        f.setValue(
                                            "endDate",
                                            getTodayWithTimeTravel()
                                                .add(35, "day")
                                                .toDate(),
                                        )
                                    }
                                    size="sm"
                                    startContent={
                                        showingExtendedForecast && (
                                            <FontAwesomeIcon icon={faCheck} />
                                        )
                                    }
                                >
                                    Extended
                                </Button>
                            )}

                            {fieldIsComplete && (
                                <Button
                                    color={
                                        showingSeasonalForecast
                                            ? "primary"
                                            : "default"
                                    }
                                    onPress={() =>
                                        f.setValue(
                                            "endDate",
                                            getTodayWithTimeTravel()
                                                .add(215, "day")
                                                .format("YYYY-MM-DD"),
                                        )
                                    }
                                    size="sm"
                                    startContent={
                                        showingSeasonalForecast && (
                                            <FontAwesomeIcon icon={faCheck} />
                                        )
                                    }
                                >
                                    Seasonal
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="text-lg font-bold">
                        {t("temperatureAndPrecipitation")}
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            data={weatherData}
                            margin={{ bottom: 8, left: 8, top: 15 }}
                            syncId="weather"
                        >
                            <XAxis
                                dataKey="date"
                                scale="time"
                                spacing={50}
                                angle={-8}
                                fontSize={12}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                            />
                            <YAxis
                                label={{
                                    value: `${t("temperature")} ${formatUnit("F", "C")}`,
                                    angle: -90,
                                    dx: -30,
                                }}
                                fontSize={12}
                            />
                            <Tooltip
                                labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                formatter={(value, name) => [
                                    `${roundTo(value as number, 1)} ${formatUnit("F", "C")}`,
                                    name === "max_temperature"
                                        ? t("chartLabels.max")
                                        : t("chartLabels.min"),
                                ]}
                            />
                            <Line
                                dataKey="max_temperature"
                                stroke="#FF5733" // warm orange-red for max temp
                                dot={false}
                            />
                            <Line
                                dataKey="min_temperature"
                                stroke="#1E90FF" // cool blue for min temp
                                dot={false}
                            />
                            {forecastReferenceArea}
                        </LineChart>
                    </ResponsiveContainer>

                    <div className="flex flex-row justify-end">
                        <Chip
                            className="text-sm text-center self-end"
                            color="primary"
                            size="sm"
                            classNames={{
                                content: "text-[0.65rem] text-center",
                            }}
                        >
                            {precipSum} {formatUnit("in", "mm")} Total
                            Precipitation
                        </Chip>
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart
                            data={weatherData}
                            margin={{ bottom: 16, left: 8, top: 15 }}
                            syncId="weather"
                        >
                            <XAxis
                                dataKey="date"
                                scale="time"
                                spacing={50}
                                angle={-8}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                fontSize={12}
                            />
                            <YAxis
                                label={{
                                    value: `${t("chartLabels.precipitation")} (${formatUnit("in", "mm")})`,
                                    angle: -90,
                                    dx: -30,
                                }}
                                fontSize={12}
                            />
                            <Tooltip
                                labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                formatter={(value) => [
                                    `${roundTo(value as number, 1)}`,
                                    `${t("chartLabels.precipitation")} (${formatUnit("in", "mm")})`,
                                ]}
                            />

                            <Bar
                                dataKey="precipitation"
                                stroke="#0000FF"
                                fill="#0000FF"
                            />
                            {forecastReferenceArea}
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="text-lg font-bold">
                        {t("accumulatedGDD")}
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            data={gddData}
                            margin={{ bottom: 16, left: 8 }}
                            syncId="weather"
                        >
                            <XAxis
                                dataKey="date"
                                scale="time"
                                spacing={50}
                                angle={-8}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                fontSize={12}
                            />
                            <YAxis
                                label={{
                                    value: "Accumulated GDD",
                                    angle: -90,
                                    dx: -30,
                                }}
                                fontSize={12}
                            />
                            <Tooltip
                                labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                formatter={(value) => [
                                    `${roundTo(value as number, 1)}`,
                                    `${t("accumulatedGDD")}`,
                                ]}
                            />

                            <Line
                                dataKey="accumulated_gdd"
                                stroke="#FFA500" // Orange, a distinct color for wind speed
                                fill="#FFA500"
                            />

                            {forecastReferenceArea}
                        </LineChart>
                    </ResponsiveContainer>
                    <div className="text-lg font-bold">
                        {t("humidityAndWindSpeed")}
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            data={weatherData}
                            margin={{ bottom: 16, left: 8, top: 15 }}
                            syncId="weather"
                        >
                            <XAxis
                                dataKey="date"
                                scale="time"
                                spacing={50}
                                angle={-8}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                fontSize={12}
                            />
                            <YAxis
                                label={{
                                    value: `${t("chartLabels.humidity")} %`,
                                    angle: -90,
                                    dx: -30,
                                }}
                                fontSize={12}
                            />
                            <Tooltip
                                labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                formatter={(value) => [
                                    `${roundTo(value as number, 1)}`,
                                    `${t("chartLabels.humidity")} %`,
                                ]}
                            />

                            <Line
                                dataKey="relative_humidity"
                                stroke="#1E90FF" // Dodger Blue, a unique color for humidity
                                fill="#1E90FF"
                            />

                            {forecastReferenceArea}
                        </LineChart>
                    </ResponsiveContainer>
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            data={weatherData}
                            margin={{ bottom: 16, left: 8, top: 15 }}
                            syncId="weather"
                        >
                            <XAxis
                                dataKey="date"
                                scale="time"
                                spacing={50}
                                angle={-8}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                fontSize={12}
                            />
                            <YAxis
                                label={{
                                    value: `${t("windSpeed")} (${formatUnit("mph", "km_h")})`,
                                    angle: -90,
                                    dx: -30,
                                }}
                                fontSize={12}
                            />
                            <Tooltip
                                labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                formatter={(value) => [
                                    `${roundTo(value as number, 1)}`,
                                    `${t("windSpeed")} (${formatUnit("mph", "km_h")})`,
                                ]}
                            />

                            <Line
                                dataKey="wind_speed"
                                stroke="#FFA500" // Orange, a distinct color for wind speed
                                fill="#FFA500"
                            />

                            {forecastReferenceArea}
                        </LineChart>
                    </ResponsiveContainer>
                </>
            )}
        </div>
    );
}

export default WeatherCharts;
